'use client'

import { memo } from 'react'
import { cn } from '@/lib/utils'
import { useNinxl8BitThemeMode, type Ninxl8BitTheme } from './use-ninxl-8bit-theme'

type PixelProps = {
  x: number
  y: number
  w?: number
  h?: number
  fill: string
}

/** baby: one logical pixel = 4 SVG units */
const P = ({ x, y, w = 1, h = 1, fill }: PixelProps) => (
  <rect x={x * 4} y={y * 4} width={w * 4} height={h * 4} fill={fill} />
)

const STARS: Array<{ x: number; y: number; delay: string }> = [
  { x: 6, y: 2, delay: '0s' },
  { x: 18, y: 5, delay: '0.6s' },
  { x: 32, y: 3, delay: '1.1s' },
  { x: 48, y: 6, delay: '0.3s' },
  { x: 64, y: 2, delay: '1.5s' },
  { x: 118, y: 4, delay: '0.9s' },
  { x: 132, y: 7, delay: '0.2s' },
  { x: 148, y: 3, delay: '1.3s' },
  { x: 155, y: 6, delay: '0.5s' },
]

type ScenePalette = {
  sky: string
  wall: string
  floor: string
  window: string
  buildingA: string
  buildingB: string
  buildingC: string
  buildingD: string
  windowLight: string
  desk: string
  deskEdge: string
  tower: string
  towerInner: string
  localLabel: string
  cable: string
  monitorBezel: string
  stand: string
  standBase: string
  keyboard: string
  key: string
  mic: string
  micBase: string
  micTip: string
  playerHead: string
  playerBody: string
  playerLeg: string
  cloud: string
  blockedStroke: string
  blockedText: string
  blockedPath: string
  shield: string
  shieldText: string
  wave: string
  celestial: string
  celestialCutout: string
}

const SCENE_PALETTE: Record<Ninxl8BitTheme, ScenePalette> = {
  dark: {
    sky: '#050505',
    wall: '#0e0e0e',
    floor: '#161616',
    window: '#080808',
    buildingA: '#1a1a1a',
    buildingB: '#222222',
    buildingC: '#1a1a1a',
    buildingD: '#2a2a2a',
    windowLight: '#ffffff',
    desk: '#5a5a5a',
    deskEdge: '#3a3a3a',
    tower: '#1a1a1a',
    towerInner: '#111111',
    localLabel: '#ffffff',
    cable: '#555555',
    monitorBezel: '#2a2a2a',
    stand: '#444444',
    standBase: '#333333',
    keyboard: '#1a1a1a',
    key: '#444444',
    mic: '#888888',
    micBase: '#666666',
    micTip: '#ffffff',
    playerHead: '#cccccc',
    playerBody: '#777777',
    playerLeg: '#555555',
    cloud: '#222222',
    blockedStroke: '#666666',
    blockedText: '#666666',
    blockedPath: '#333333',
    shield: '#ffffff',
    shieldText: '#ffffff',
    wave: '#444444',
    celestial: '#dddddd',
    celestialCutout: '#050505',
  },
  light: {
    sky: '#c8e4f4',
    wall: '#f0ebe3',
    floor: '#d8d0c4',
    window: '#e8f4fc',
    buildingA: '#c5d0d8',
    buildingB: '#b0bcc8',
    buildingC: '#a8b4c0',
    buildingD: '#98a8b8',
    windowLight: '#21728d',
    desk: '#a08060',
    deskEdge: '#806040',
    tower: '#e8e4dc',
    towerInner: '#d8d0c4',
    localLabel: '#0a1018',
    cable: '#88a1ac',
    monitorBezel: '#444444',
    stand: '#666666',
    standBase: '#888888',
    keyboard: '#d8d0c4',
    key: '#a09890',
    mic: '#5f7380',
    micBase: '#88a1ac',
    micTip: '#21728d',
    playerHead: '#5f7380',
    playerBody: '#88a1ac',
    playerLeg: '#5f7380',
    cloud: '#d0e4f0',
    blockedStroke: '#88a1ac',
    blockedText: '#5f7380',
    blockedPath: '#c5d0d8',
    shield: '#21728d',
    shieldText: '#21728d',
    wave: '#88a1ac',
    celestial: '#f5d76e',
    celestialCutout: '#c8e4f4',
  },
}

/** Animated 8-bit hero: desk + NINX app on screen (tool approval moment) */
export const Ninxl8BitDesktopScene = memo(function Ninxl8BitDesktopScene({
  className,
  theme: themeProp,
}: {
  className?: string
  theme?: Ninxl8BitTheme
}) {
  const contextTheme = useNinxl8BitThemeMode()
  const theme = themeProp ?? contextTheme
  const p = SCENE_PALETTE[theme]
  const isDark = theme === 'dark'

  return (
    <svg
      viewBox="0 0 640 360"
      className={cn('ninxl-pixel-scene h-full w-full', className)}
      aria-hidden
      preserveAspectRatio="xMidYMid slice"
    >
      <rect width="640" height="360" fill={p.sky} />

      {isDark ? (
        STARS.map((star) => (
          <rect
            key={`${star.x}-${star.y}`}
            x={star.x * 4}
            y={star.y * 4}
            width={4}
            height={4}
            fill="#fff"
            className="ninxl-8bit-star"
            style={{ animationDelay: star.delay }}
          />
        ))
      ) : null}

      <g className="ninxl-8bit-moon-glow">
        <P x={152} y={4} w={5} h={5} fill={p.celestial} />
        {isDark ? <P x={153} y={5} w={3} h={3} fill={p.celestialCutout} /> : null}
      </g>

      <P x={0} y={38} w={160} h={52} fill={p.wall} />
      <P x={0} y={82} w={160} h={8} fill={p.floor} />

      <P x={118} y={14} w={32} h={26} fill={p.window} />
      <P x={122} y={30} w={6} h={14} fill={p.buildingA} />
      <P x={130} y={26} w={5} h={18} fill={p.buildingB} />
      <P x={137} y={22} w={7} h={22} fill={p.buildingC} />
      <P x={146} y={28} w={5} h={16} fill={p.buildingD} />
      <P x={132} y={26} w={1} h={1} fill={p.windowLight} />
      <P x={140} y={30} w={1} h={1} fill={p.windowLight} />

      <P x={14} y={56} w={90} h={5} fill={p.desk} />
      <P x={12} y={61} w={94} h={2} fill={p.deskEdge} />

      <g>
        <P x={16} y={38} w={12} h={20} fill={p.tower} />
        <P x={17} y={39} w={10} h={18} fill={p.towerInner} />
        <text x={72} y={168} fill={p.localLabel} fontSize="6" fontFamily="monospace" fontWeight="bold">
          LOCAL
        </text>
        <rect
          x={76}
          y={160}
          width={8}
          height={8}
          fill={isDark ? '#fff' : '#21728d'}
          className="ninxl-8bit-blink"
        />
        <P x={19} y={44} w={5} h={5} fill={isDark ? '#fff' : '#21728d'} />
        <P x={20} y={43} w={3} h={1} fill={isDark ? '#fff' : '#21728d'} />
        <path
          d="M 112 200 L 160 200 L 200 188"
          fill="none"
          stroke={p.cable}
          strokeWidth="2"
          strokeDasharray="6 4"
          className="ninxl-8bit-data-cable"
        />
      </g>

      <g className="ninxl-8bit-monitor-glow">
        <P x={38} y={18} w={58} h={36} fill={p.monitorBezel} />
        <P x={40} y={20} w={54} h={32} fill="#000" />

        <rect x={164} y={84} width={6} height={6} fill="#fff" />
        <rect x={174} y={84} width={6} height={6} fill="#888" />
        <rect x={184} y={84} width={6} height={6} fill="#444" />
        <text x={200} y={90} fill="#888" fontSize="5" fontFamily="monospace">
          NINX: Local Session
        </text>

        <P x={41} y={24} w={10} h={26} fill="#111" />
        <P x={42} y={26} w={8} h={2} fill="#333" />
        <P x={42} y={30} w={8} h={2} fill="#222" />
        <rect x={168} y={136} width={32} height={12} fill="#fff" className="ninxl-8bit-chat-active" />
        <P x={42} y={40} w={8} h={2} fill="#333" />
        <P x={42} y={44} w={8} h={2} fill="#222" />

        <P x={52} y={24} w={32} h={26} fill="#0a0a0a" />
        <P x={54} y={28} w={14} h={4} fill="#333" />
        <text x={220} y={116} fill="#666" fontSize="4" fontFamily="monospace">
          organize Q3 emails
        </text>
        <P x={66} y={34} w={16} h={5} fill="#fff" />
        <text x={268} y={142} fill="#000" fontSize="4" fontFamily="monospace">
          Found 12 threads locally
        </text>
        <P x={54} y={42} w={12} h={3} fill="#222" />

        <g className="ninxl-8bit-approval-pulse">
          <P x={58} y={46} w={20} h={8} fill="#1a1a1a" />
          <P x={59} y={47} w={18} h={6} fill="#111" />
          <text x={240} y={192} fill="#aaa" fontSize="4" fontFamily="monospace">
            approve: search_gmail
          </text>
          <rect x={312} y={196} width={20} height={8} fill="#fff" className="ninxl-8bit-blink" />
          <text x={316} y={202} fill="#000" fontSize="4" fontFamily="monospace" fontWeight="bold">
            ALLOW
          </text>
        </g>

        <P x={85} y={24} w={8} h={26} fill="#111" />
        <rect x={344} y={100} width={24} height={6} fill="#222" stroke="#555" strokeWidth={0.5} />
        <text x={348} y={105} fill="#aaa" fontSize="3.5" fontFamily="monospace">
          gmail
        </text>
        <rect x={344} y={110} width={24} height={6} fill="#222" stroke="#555" strokeWidth={0.5} />
        <text x={348} y={115} fill="#aaa" fontSize="3.5" fontFamily="monospace">
          shell
        </text>

        <P x={54} y={56} w={28} h={3} fill="#1a1a1a" />
        <rect x={220} y={228} width={4} height={8} fill="#fff" className="ninxl-8bit-blink" />
      </g>

      <P x={62} y={54} w={10} h={4} fill={p.stand} />
      <P x={58} y={58} w={18} h={2} fill={p.standBase} />

      <P x={52} y={60} w={30} h={3} fill={p.keyboard} />
      {Array.from({ length: 10 }).map((_, i) => (
        <P key={i} x={53 + i * 3} y={61} w={2} h={1} fill={p.key} />
      ))}
      <g className="ninxl-8bit-mic-pulse">
        <P x={86} y={58} w={2} h={4} fill={p.mic} />
        <P x={85} y={62} w={4} h={1} fill={p.micBase} />
        <P x={86} y={57} w={2} h={1} fill={p.micTip} />
      </g>

      <g className="ninxl-8bit-player-idle">
        <P x={98} y={50} w={3} h={3} fill={p.playerHead} />
        <P x={97} y={53} w={5} h={4} fill={p.playerBody} />
        <P x={96} y={57} w={2} h={3} fill={p.playerLeg} />
        <P x={100} y={57} w={2} h={3} fill={p.playerLeg} />
      </g>

      <g opacity={0.75}>
        <P x={124} y={10} w={12} h={5} fill={p.cloud} />
        <P x={126} y={8} w={4} h={2} fill={p.cloud} />
        <P x={131} y={8} w={4} h={2} fill={p.cloud} />
        <line x1={500} y1={48} x2={520} y2={68} stroke={p.blockedStroke} strokeWidth={3} />
        <line x1={520} y1={48} x2={500} y2={68} stroke={p.blockedStroke} strokeWidth={3} />
        <text x={488} y={82} fill={p.blockedText} fontSize="5" fontFamily="monospace">
          CLOUD BLOCKED
        </text>
        <path
          d="M 480 60 Q 520 20 560 60"
          fill="none"
          stroke={p.blockedPath}
          strokeWidth="1.5"
          strokeDasharray="4 6"
        />
      </g>

      <path
        d="M 80 120 L 80 220 L 200 250 L 320 220 L 320 120 L 200 90 Z"
        fill="none"
        stroke={p.shield}
        strokeWidth="1.5"
        opacity={isDark ? 0.12 : 0.2}
        className="ninxl-8bit-shield-pulse"
      />
      <text
        x={168}
        y={168}
        fill={p.shieldText}
        fontSize="7"
        fontFamily="monospace"
        opacity={isDark ? 0.35 : 0.45}
      >
        ON-DEVICE
      </text>

      <g className="ninxl-8bit-wave-line" opacity={0.25}>
        {Array.from({ length: 24 }).map((_, i) => (
          <rect key={i} x={i * 28} y={332} width={12} height={2} fill={p.wave} />
        ))}
      </g>
    </svg>
  )
})